import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
    Sparkles,
    Play,
    Clock,
    History,
    Trophy,
    Flame,
    FileText,
    Timer,
    Lock,
    BookOpen,
    ArrowRight,
    Target,
    Check,
    ChevronDown,
} from "lucide-react";
import Layout from "../components/Layout";
import api from "../lib/api";

const DIFFICULTY_LABELS = ["", "Très facile", "Facile", "Moyen", "Difficile", "Expert"];

function formatDuration(secs) {
    if (!secs || secs < 1) return "—";
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    if (m === 0) return `${s}s`;
    if (s === 0) return `${m} min`;
    return `${m} min ${s}s`;
}

export default function SimulatorHome() {
    const [profiles, setProfiles] = useState([]);
    const [history, setHistory] = useState([]);
    const [starting, setStarting] = useState(null);
    const [access, setAccess] = useState(null); // null=loading, {allowed, requirement, best_score}
    // Iter-27.53 — Mastery par profil (badges + stat globale)
    const [mastery, setMastery] = useState({ by_profile: {}, mastered_count: 0 });
    // Iter-29 — Sélecteur de cible d'entraînement (asso ou générique)
    const [targets, setTargets] = useState({ targets: [], association_count: 0, default_target_id: "generic" });
    const [selectedTarget, setSelectedTarget] = useState("generic");
    const [targetMenuOpen, setTargetMenuOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/simulator/profiles").then((r) => setProfiles(r.data));
        api.get("/simulator/sessions/me").then((r) => setHistory(r.data));
        api.get("/simulator/access").then((r) => setAccess(r.data));
        api.get("/simulator/mastery").then((r) => setMastery(r.data));
        api.get("/simulator/training-targets").then((r) => {
            setTargets(r.data);
            setSelectedTarget(r.data.default_target_id || "generic");
        });
    }, []);

    const showTargetSelector = (targets.association_count || 0) >= 2;
    const currentTarget = useMemo(
        () => (targets.targets || []).find((t) => t.id === selectedTarget) || targets.targets?.[0],
        [targets, selectedTarget],
    );

    const start = async (profile_id) => {
        if (access && !access.allowed) {
            toast.error("Termine d'abord le quiz « L'art du don régulier » avec ≥ 70 %");
            return;
        }
        setStarting(profile_id);
        try {
            const payload = { profile_id };
            // Iter-29 — Envoie l'association_id si une asso (pas le mode générique)
            // est sélectionnée, pour adapter le contexte du donateur virtuel.
            if (selectedTarget && selectedTarget !== "generic") {
                payload.association_id = selectedTarget;
            }
            const { data } = await api.post("/simulator/sessions", payload);
            navigate(`/simulator/session/${data.session_id}`);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.error === "simulator_locked") {
                setAccess({
                    allowed: false,
                    reason: detail.reason,
                    requirement: detail.requirement,
                    best_score: detail.best_score,
                });
                toast.error(
                    "Tu dois d'abord valider le module « L'art du don régulier » avec un score ≥ 70 %",
                );
            } else {
                toast.error("Impossible de démarrer la simulation");
            }
            setStarting(null);
        }
    };

    const locked = access && !access.allowed;

    return (
        <Layout
            title="Simulateur IA"
            subtitle="Entraîne-toi face à un donateur virtuel"
        >
            {/* Hero */}
            <div className="relative rounded-3xl p-6 lg:p-8 mb-8 overflow-hidden bg-gradient-to-br from-shariti-orange to-shariti-orange-light text-white">
                <div className="absolute -bottom-10 -right-10 w-48 h-48 rounded-full bg-shariti-yellow/30 blur-3xl" />
                <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-white/10 blur-3xl" />
                <div className="relative z-10 grid lg:grid-cols-2 gap-6 items-center">
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="w-5 h-5" />
                            <p className="text-xs font-thin tracking-[0.3em] uppercase opacity-90">
                                Entraînement immersif IA
                            </p>
                        </div>
                        <h2 className="text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight mb-3">
                            Affronte des donateurs virtuels qui ne te feront pas de cadeau
                        </h2>
                        <p className="font-light opacity-90">
                            Voix, objections, mise sous pression — comme sur le terrain. Score
                            détaillé en fin de session : pitch, objections, closing, axes
                            d'amélioration.
                        </p>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        {[
                            { label: "Profils", value: profiles.length },
                            {
                                label: "Sessions",
                                value: history.length,
                            },
                            {
                                label: "Meilleur",
                                value: `${Math.max(0, ...history.map((h) => h?.score?.overall || 0))}%`,
                            },
                        ].map((s) => (
                            <div
                                key={s.label}
                                className="bg-shariti-black/30 backdrop-blur rounded-2xl p-3 text-center"
                            >
                                <p className="text-2xl font-extrabold leading-none">
                                    {s.value}
                                </p>
                                <p className="text-[10px] font-thin tracking-widest uppercase opacity-90 mt-1">
                                    {s.label}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Iter-27.18 — Lock state when the gating module isn't validated */}
            {locked && (
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid="simulator-lock-card"
                    className="relative rounded-3xl p-6 lg:p-7 mb-8 overflow-hidden border border-shariti-yellow/30 bg-gradient-to-br from-shariti-yellow/8 via-shariti-orange/5 to-transparent"
                >
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-shariti-yellow/15 flex items-center justify-center shrink-0">
                            <Lock className="w-6 h-6 text-shariti-yellow" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-yellow mb-1">
                                Simulateur verrouillé
                            </p>
                            <h3 className="text-xl lg:text-2xl font-extrabold text-white tracking-tight mb-2">
                                Avant de t'entraîner, valide les fondamentaux
                            </h3>
                            <p className="text-sm text-white/70 leading-relaxed mb-4 max-w-2xl font-light">
                                Le simulateur reproduit fidèlement le terrain : objections,
                                pression, posture. Pour qu'il te serve vraiment, on a besoin que
                                tu maîtrises d'abord le script et les règles de vigilance.
                                Termine le quiz du module{" "}
                                <span className="font-bold text-white">
                                    « {access.requirement.module_title} »
                                </span>{" "}
                                avec un score d'au moins{" "}
                                <span className="font-bold text-shariti-yellow">
                                    {access.requirement.min_score} %
                                </span>{" "}
                                pour débloquer l'entraînement.
                            </p>
                            <div className="flex flex-wrap gap-3 items-center">
                                <Link
                                    to={`/module/${access.requirement.module_id}`}
                                    data-testid="simulator-lock-cta"
                                    className="inline-flex items-center gap-2 bg-shariti-orange text-white font-extrabold py-3 px-5 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all"
                                >
                                    <BookOpen className="w-4 h-4" />
                                    Aller au module
                                    <ArrowRight className="w-4 h-4" />
                                </Link>
                                <span className="text-xs text-white/55">
                                    Score actuel :{" "}
                                    <span className="font-bold text-white">
                                        {access.best_score}%
                                    </span>{" "}
                                    · Cible : {access.requirement.min_score}%
                                </span>
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Iter-29 — Sélecteur de cible d'entraînement (visible si ≥2 assos) */}
            {showTargetSelector && !locked && (
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="relative mb-8"
                    data-testid="simulator-target-selector"
                >
                    <div className="rounded-3xl border border-shariti-orange/25 bg-gradient-to-br from-shariti-orange/12 via-shariti-orange/5 to-transparent p-5 sm:p-6">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="shrink-0 w-10 h-10 rounded-2xl bg-shariti-orange/20 flex items-center justify-center">
                                    <Target className="w-5 h-5 text-shariti-orange-light" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light mb-0.5">
                                        Je m&apos;entraîne pour
                                    </p>
                                    <p className="text-base sm:text-lg font-extrabold text-white truncate">
                                        {currentTarget?.name || "Mode générique Shariti"}
                                    </p>
                                </div>
                            </div>
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={() => setTargetMenuOpen((v) => !v)}
                                    data-testid="simulator-target-button"
                                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-white/8 hover:bg-white/12 border border-white/15 text-sm font-bold text-white transition-all"
                                >
                                    Changer
                                    <ChevronDown className={`w-4 h-4 transition-transform ${targetMenuOpen ? "rotate-180" : ""}`} />
                                </button>
                                <AnimatePresence>
                                    {targetMenuOpen && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -4 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -4 }}
                                            transition={{ duration: 0.15 }}
                                            className="absolute right-0 top-full mt-2 w-[320px] max-w-[80vw] bg-shariti-deep-black/95 backdrop-blur-xl border border-white/15 rounded-2xl shadow-2xl shadow-black/50 z-50 overflow-hidden"
                                            data-testid="simulator-target-menu"
                                        >
                                            <div className="max-h-[400px] overflow-y-auto py-1">
                                                {(targets.targets || []).map((t) => {
                                                    const isSelected = t.id === selectedTarget;
                                                    return (
                                                        <button
                                                            key={t.id}
                                                            type="button"
                                                            onClick={() => {
                                                                setSelectedTarget(t.id);
                                                                setTargetMenuOpen(false);
                                                            }}
                                                            data-testid={`simulator-target-option-${t.id}`}
                                                            className={`w-full text-left px-3 py-2.5 flex items-start gap-3 transition-colors ${
                                                                isSelected
                                                                    ? "bg-shariti-orange/15"
                                                                    : "hover:bg-white/5"
                                                            }`}
                                                        >
                                                            {t.logo_url ? (
                                                                <img
                                                                    src={t.logo_url}
                                                                    alt=""
                                                                    className="w-9 h-9 rounded-xl object-cover bg-white/5 shrink-0"
                                                                />
                                                            ) : (
                                                                <div className={`w-9 h-9 rounded-xl shrink-0 flex items-center justify-center ${
                                                                    t.kind === "generic"
                                                                        ? "bg-shariti-yellow/20 text-shariti-yellow"
                                                                        : "bg-shariti-orange/20 text-shariti-orange-light"
                                                                }`}>
                                                                    {t.kind === "generic" ? <Sparkles className="w-4 h-4" /> : <Target className="w-4 h-4" />}
                                                                </div>
                                                            )}
                                                            <div className="flex-1 min-w-0">
                                                                <p className={`text-sm font-bold leading-tight ${isSelected ? "text-shariti-orange-light" : "text-white"}`}>
                                                                    {t.name}
                                                                </p>
                                                                {t.subtitle && (
                                                                    <p className="text-[11px] text-white/55 mt-0.5 truncate font-light">
                                                                        {t.subtitle}
                                                                    </p>
                                                                )}
                                                            </div>
                                                            {isSelected && (
                                                                <Check className="w-4 h-4 text-shariti-orange-light shrink-0 mt-1" />
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>
                        {currentTarget?.kind === "association" && (
                            <p className="text-xs text-white/55 font-light mt-3 leading-relaxed">
                                Les donateurs virtuels poseront des objections crédibles et
                                spécifiques à <span className="font-bold text-white">{currentTarget.name}</span>.
                            </p>
                        )}
                        {currentTarget?.kind === "generic" && (
                            <p className="text-xs text-white/55 font-light mt-3 leading-relaxed">
                                Tu travailles tes compétences transversales — sans contexte
                                d&apos;asso spécifique.
                            </p>
                        )}
                    </div>
                </motion.div>
            )}

            {/* Profiles */}
            <div className="flex items-end justify-between gap-3 mb-5 flex-wrap">
                <div>
                    <p className="text-xs font-thin tracking-[0.25em] uppercase text-shariti-orange-light mb-2">
                        Choisir un profil de donateur
                    </p>
                    <h3 className="text-2xl font-extrabold text-white">
                        {profiles.length} comportements à affronter
                    </h3>
                </div>
                {/* Iter-27.53 — Stat globale de mastery */}
                {profiles.length > 0 && (
                    <div
                        className="glass-card rounded-2xl px-4 py-2.5 inline-flex items-center gap-3"
                        data-testid="simulator-mastery-stat"
                    >
                        <Trophy className="w-5 h-5 text-shariti-yellow" />
                        <div>
                            <p className="text-[10px] font-thin tracking-[0.18em] uppercase text-white/45">
                                Profils maîtrisés
                            </p>
                            <p className="text-lg font-extrabold text-white tabular-nums leading-none mt-0.5">
                                {mastery.mastered_count}
                                <span className="text-white/40 text-sm">/{profiles.length}</span>
                            </p>
                        </div>
                    </div>
                )}
            </div>
            <div
                className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-10"
                data-testid="simulator-profiles"
            >
                {profiles.map((p, i) => {
                    const m = mastery.by_profile?.[p.profile_id];
                    const hasTier = m && m.tier !== "none";
                    return (
                        <motion.button
                            key={p.profile_id}
                            onClick={() => start(p.profile_id)}
                            disabled={starting !== null || locked}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: i * 0.05 }}
                            data-testid={`simulator-profile-${p.profile_id}`}
                            className={`text-left glass-card glass-card-hover rounded-3xl p-5 transition-all relative group ${
                                locked
                                    ? "opacity-40 cursor-not-allowed"
                                    : "disabled:opacity-60 disabled:cursor-wait"
                            }`}
                        >
                            {/* Iter-27.53 — Badge de mastery (top-right) */}
                            {hasTier && (
                                <div
                                    className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/8 border border-white/10 text-[11px] font-extrabold uppercase tracking-wider"
                                    data-testid={`simulator-mastery-badge-${p.profile_id}`}
                                    title={`${m.wins} conversion${m.wins > 1 ? "s" : ""} sur ${m.attempts} tentative${m.attempts > 1 ? "s" : ""} — Meilleur score : ${m.best_overall}%`}
                                >
                                    <span className="text-sm leading-none">{m.emoji}</span>
                                    <span className="text-white/85">{m.label}</span>
                                </div>
                            )}
                            <div
                                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 text-2xl"
                                style={{
                                    background: `linear-gradient(135deg, ${p.avatar_color}, ${p.avatar_color}aa)`,
                                }}
                            >
                                <span className="font-extrabold text-shariti-black">
                                    {p.name?.[0]}
                                </span>
                            </div>
                            <div className="flex items-center justify-between mb-2 gap-2">
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-lg font-extrabold text-white tracking-tight truncate">
                                        {p.name}
                                    </h4>
                                    {/* Iter-27.53 — Nom du donateur visible (Marie Dubois, etc.) */}
                                    {p.donor_name && (
                                        <p className="text-[11px] text-white/45 font-light tracking-wide truncate">
                                            {p.donor_name}
                                            {p.age ? ` · ${p.age} ans` : ""}
                                        </p>
                                    )}
                                </div>
                                <div className="flex gap-0.5 shrink-0">
                                    {[1, 2, 3, 4, 5].map((d) => (
                                        <Flame
                                            key={d}
                                            className={`w-3 h-3 ${
                                                d <= p.difficulty
                                                    ? "text-shariti-orange fill-shariti-orange"
                                                    : "text-white/15"
                                            }`}
                                        />
                                    ))}
                                </div>
                            </div>
                            <p className="text-sm text-white/60 font-light mb-4 min-h-[2.5rem]">
                                {p.description}
                            </p>
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-thin tracking-widest uppercase text-shariti-orange-light">
                                    {DIFFICULTY_LABELS[p.difficulty]}
                                    {hasTier && (
                                        <span className="ml-2 text-white/40 normal-case tracking-normal">
                                            · {m.wins}/{m.attempts} conv.
                                        </span>
                                    )}
                                </span>
                                <span className="inline-flex items-center gap-1 text-shariti-orange font-bold text-sm">
                                    {starting === p.profile_id ? (
                                        "Démarrage..."
                                    ) : (
                                        <>
                                            Lancer
                                            <Play className="w-3 h-3 fill-current" />
                                        </>
                                    )}
                                </span>
                            </div>
                        </motion.button>
                    );
                })}
            </div>

            {/* History */}
            {history.length > 0 && (
                <>
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <p className="text-xs font-thin tracking-[0.25em] uppercase text-shariti-orange-light mb-1">
                                Historique
                            </p>
                            <h3 className="text-2xl font-extrabold text-white">
                                Tes sessions
                            </h3>
                        </div>
                        <History className="w-6 h-6 text-shariti-orange-light" />
                    </div>
                    <div className="space-y-2" data-testid="simulator-history">
                        {history.map((s) => (
                            <button
                                key={s.session_id}
                                onClick={() => navigate(`/simulator/session/${s.session_id}`)}
                                data-testid={`simulator-history-${s.session_id}`}
                                className="w-full text-left flex items-start gap-4 p-4 rounded-2xl glass-card glass-card-hover transition-all"
                            >
                                <div
                                    className="w-10 h-10 rounded-xl flex items-center justify-center font-extrabold text-shariti-black shrink-0 mt-0.5"
                                    style={{
                                        background: s.profile_avatar_color || "#EE722E",
                                    }}
                                >
                                    {s.profile_name?.[0]}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-white text-sm truncate">
                                        {s.profile_name}
                                    </p>
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-white/40 mt-0.5">
                                        <span className="inline-flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            {new Date(s.started_at).toLocaleDateString("fr-FR", {
                                                day: "numeric",
                                                month: "short",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })}
                                        </span>
                                        {s.duration_seconds > 0 && (
                                            <span
                                                className="inline-flex items-center gap-1 text-shariti-orange-light font-bold"
                                                data-testid={`simulator-history-duration-${s.session_id}`}
                                            >
                                                <Timer className="w-3 h-3" />
                                                {formatDuration(s.duration_seconds)}
                                            </span>
                                        )}
                                    </div>
                                    {s.summary && (
                                        <p
                                            className="text-xs text-white/60 font-light mt-2 flex items-start gap-1.5 leading-relaxed"
                                            data-testid={`simulator-history-summary-${s.session_id}`}
                                        >
                                            <FileText className="w-3 h-3 mt-0.5 text-shariti-orange-light shrink-0" />
                                            <span>{s.summary}</span>
                                        </p>
                                    )}
                                </div>
                                {s.ended_at ? (
                                    <div className="flex items-center gap-1.5 shrink-0 mt-0.5">
                                        <Trophy className="w-4 h-4 text-shariti-orange" />
                                        <span className="text-lg font-extrabold text-shariti-orange">
                                            {s?.score?.overall || 0}%
                                        </span>
                                    </div>
                                ) : (
                                    <span className="text-[10px] font-extrabold uppercase tracking-widest px-2 py-1 rounded-full bg-shariti-yellow/20 text-shariti-yellow shrink-0">
                                        En cours
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </Layout>
    );
}
